// Per-message feedback widget.
//
// Collapsed pill expands to three 1..5 ratings (relevance / correctness /
// completeness), an optional comment, and "what worked / what didn't"
// fields. Submission upserts via POST /feedback (the backend keys on
// turn_id, so re-submitting edits the row in place).

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  getFeedback,
  postFeedback,
  type FeedbackPayload,
  type FeedbackRecord,
} from '../../lib/feedbackApi';

type Dim = 'relevance' | 'correctness' | 'completeness';

const DIMENSIONS: { key: Dim; label: string; hint: string }[] = [
  { key: 'relevance', label: 'Relevance', hint: 'Did it address your question?' },
  { key: 'correctness', label: 'Correctness', hint: 'Was the answer factually accurate?' },
  { key: 'completeness', label: 'Completeness', hint: 'Did it cover the key points?' },
];

interface FeedbackFormProps {
  turnId: string;
}

export function FeedbackForm({ turnId }: FeedbackFormProps) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [relevance, setRelevance] = useState<number | null>(null);
  const [correctness, setCorrectness] = useState<number | null>(null);
  const [completeness, setCompleteness] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [whatWentWell, setWhatWentWell] = useState('');
  const [whatDidnt, setWhatDidnt] = useState('');

  // Lazy-load any existing feedback when the panel is first opened.
  useEffect(() => {
    if (!open || loaded) return;
    let cancelled = false;
    getFeedback(turnId)
      .then((row: FeedbackRecord | null) => {
        if (cancelled || !row) return;
        setRelevance(row.relevance);
        setCorrectness(row.correctness);
        setCompleteness(row.completeness);
        setComment(row.comment ?? '');
        setWhatWentWell(row.what_went_well ?? '');
        setWhatDidnt(row.what_didnt ?? '');
      })
      .catch(() => {
        /* 404 → no prior feedback; other errors are non-fatal here */
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, loaded, turnId]);

  const setDim = (dim: Dim, value: number) => {
    if (dim === 'relevance') setRelevance(value);
    if (dim === 'correctness') setCorrectness(value);
    if (dim === 'completeness') setCompleteness(value);
  };

  const valueFor = (dim: Dim): number | null => {
    if (dim === 'relevance') return relevance;
    if (dim === 'correctness') return correctness;
    return completeness;
  };

  const hasAny =
    relevance != null ||
    correctness != null ||
    completeness != null ||
    comment.trim() !== '' ||
    whatWentWell.trim() !== '' ||
    whatDidnt.trim() !== '';

  const submit = async () => {
    if (!hasAny) {
      toast.error('Add at least one rating or note before submitting.');
      return;
    }
    setSubmitting(true);
    const body: FeedbackPayload = {
      turn_id: turnId,
      relevance,
      correctness,
      completeness,
      comment: comment.trim() || null,
      what_went_well: whatWentWell.trim() || null,
      what_didnt: whatDidnt.trim() || null,
    };
    try {
      await postFeedback(body);
      toast.success('Feedback saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        className="malpedia-feedback-pill"
        onClick={() => setOpen(true)}
        title="Rate this answer"
      >
        ☆ Rate this answer
      </button>
    );
  }

  return (
    <div className="malpedia-feedback-form">
      <div className="malpedia-feedback-form-header">
        <span className="malpedia-feedback-form-title">Rate this answer</span>
        <button
          type="button"
          className="malpedia-feedback-form-close"
          onClick={() => setOpen(false)}
          aria-label="Close feedback"
        >
          ×
        </button>
      </div>

      {DIMENSIONS.map(({ key, label, hint }) => (
        <div key={key} className="malpedia-feedback-dim">
          <div className="malpedia-feedback-dim-label">
            <span>{label}</span>
            <span className="malpedia-feedback-dim-hint">{hint}</span>
          </div>
          <div className="malpedia-feedback-stars" role="radiogroup" aria-label={label}>
            {[1, 2, 3, 4, 5].map((n) => {
              const active = (valueFor(key) ?? 0) >= n;
              return (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={valueFor(key) === n}
                  className={
                    'malpedia-feedback-star' + (active ? ' is-active' : '')
                  }
                  onClick={() => setDim(key, n)}
                  title={`${n} of 5`}
                >
                  ★
                </button>
              );
            })}
            {valueFor(key) != null && (
              <button
                type="button"
                className="malpedia-feedback-clear"
                onClick={() => setDim(key, 0)}
                title="Clear"
              >
                clear
              </button>
            )}
          </div>
        </div>
      ))}

      <textarea
        className="malpedia-feedback-textarea"
        placeholder="Comment (optional)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={2}
      />
      <textarea
        className="malpedia-feedback-textarea"
        placeholder="What worked well?"
        value={whatWentWell}
        onChange={(e) => setWhatWentWell(e.target.value)}
        rows={2}
      />
      <textarea
        className="malpedia-feedback-textarea"
        placeholder="What didn't work?"
        value={whatDidnt}
        onChange={(e) => setWhatDidnt(e.target.value)}
        rows={2}
      />

      <div className="malpedia-feedback-form-footer">
        <button
          type="button"
          className="malpedia-feedback-submit"
          onClick={submit}
          disabled={submitting || !hasAny}
        >
          {submitting ? 'Saving…' : 'Save feedback'}
        </button>
      </div>
    </div>
  );
}
